import { CalendarRange } from 'lucide-react'
import PropTypes from 'prop-types'
import { useState, useMemo, useCallback } from 'react'

import { FormModal, FormModalFooter } from '@components/branded-modal'
import { AdasDatePicker } from '@components/wrapper-components'
import { BUTTONS_DISPLAY_LABEL, DATE_FORMATS } from '../../../constants'
import { formatDate } from '../../../utils/utils'

export const DatePickerModal = ({
  open,
  handleClose,
  title,
  cancelLabel = BUTTONS_DISPLAY_LABEL.CANCEL,
  confirmLabel,
  message,
  dateRangeLimit = 2,
  handleSubmit,
  dateRangeLimitWarning
}) => {
  const [startDate, setStartDate] = useState()
  const [endDate, setEndDate] = useState()
  const [errorMessage, setErrorMessage] = useState()

  const handleCloseModal = useCallback(() => {
    setStartDate('')
    setEndDate('')
    setErrorMessage('')
    handleClose()
  }, [setStartDate, setEndDate, setErrorMessage, handleClose])

  const handleSubmitModal = useCallback(
    (startDate, endDate) => {
      handleSubmit(startDate, endDate).then(() => {
        // Clear fields after successful submission
        setStartDate('')
        setEndDate('')
        setErrorMessage('')
      })
    },
    [handleSubmit, setStartDate, setEndDate, setErrorMessage]
  )

  const formattedStartDate = useMemo(() => formatDate(startDate, DATE_FORMATS.ISO_DATE_STRING), [startDate])
  const formattedEndDate = useMemo(() => formatDate(endDate, DATE_FORMATS.ISO_DATE_STRING), [endDate])

  const handleStartDateChange = useCallback(
    (newDate) => {
      const startDate = new Date(newDate)
      const endDateTemp = new Date(endDate)

      //Setting the max date range with in limit
      const maxSelectableEndDate = new Date(startDate)
      maxSelectableEndDate.setMonth(maxSelectableEndDate.getMonth() + dateRangeLimit)
      maxSelectableEndDate.setHours(23, 59, 59, 999)

      if (endDateTemp && endDateTemp <= startDate) {
        setErrorMessage('Start date must be less than end date')
      } else if (endDateTemp && endDateTemp >= maxSelectableEndDate) {
        setErrorMessage(dateRangeLimitWarning)
      } else {
        setErrorMessage('') // doing this since date picker shows the last clicked date even it's not set in state
      }
      let formattedDate = formatDate(startDate)
      setStartDate(formattedDate)
    },
    [endDate]
  )

  const handleEndDateChange = useCallback(
    (newDate) => {
      const endDate = new Date(newDate).setHours(23, 59, 59, 999)
      const startDateTemp = new Date(startDate)

      //Setting the max date range with in limit
      const maxSelectableEndDate = new Date(startDate)
      maxSelectableEndDate.setMonth(maxSelectableEndDate.getMonth() + dateRangeLimit)

      if (startDateTemp && endDate <= startDateTemp) {
        setErrorMessage('End date must be greater than start date')
      } else if (maxSelectableEndDate && endDate > maxSelectableEndDate) {
        setErrorMessage(dateRangeLimitWarning)
      } else {
        setErrorMessage('')
      }
      const formattedDate = formatDate(endDate)
      setEndDate(formattedDate)
    },
    [startDate]
  )

  // CAR-4050: the standard pop-up (FormModal + FormModalFooter); behaviour and props unchanged.
  return (
    <FormModal
      open={open}
      onClose={handleCloseModal}
      icon={CalendarRange}
      title={title}
      width='max-w-[560px]'
      errorMessage={errorMessage || undefined}
      footer={
        <FormModalFooter
          onCancel={handleCloseModal}
          cancelLabel={cancelLabel}
          onConfirm={() => handleSubmitModal(startDate, endDate)}
          confirmLabel={confirmLabel}
          confirmDisabled={!!errorMessage || !startDate || !endDate}
        />
      }
    >
      <p className='mb-4 text-[13px] leading-5 text-[#334155]'>{message}</p>
      <div className='flex flex-col gap-4 sm:flex-row'>
        <AdasDatePicker
          label='Start Date'
          variant='outlined'
          name='start_date'
          disableFuture={true}
          className='flex-1'
          value={formattedStartDate}
          format={DATE_FORMATS.ISO_DATE_STRING}
          onChange={handleStartDateChange}
        />
        <AdasDatePicker
          disableFuture={true}
          label='End Date'
          variant='outlined'
          name='end_date'
          className='flex-1'
          value={formattedEndDate}
          format={DATE_FORMATS.ISO_DATE_STRING}
          onChange={handleEndDateChange}
        />
      </div>
    </FormModal>
  )
}

DatePickerModal.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  confirmLabel: PropTypes.string.isRequired,
  message: PropTypes.string.isRequired,
  cancelLabel: PropTypes.string,
  dateRangeLimitWarning: PropTypes.string
}
